import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton, 
  Typography,
  TextField,
  Button,
  CircularProgress,
  styled,
  Box,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { CreateButton, CustomDivider, DividerWrapper, ProjectDescriptionField, ProjectDialog, ProjectDialogActions, ProjectDialogContent, ProjectDialogTitle, ProjectTitleField, RequiredMark, SectionLabel, WarningText } from './style';

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onProjectCreated: (projectId: string) => void;
  title?: string;
  placeholderName?: string;
  placeholderDescription?: string;
}

export default function CreateProjectModal({ 
  open, 
  onClose, 
  onProjectCreated, 
  title = 'Criar Projeto', 
  placeholderName = 'Nome do projeto',
  placeholderDescription = 'Descrição do projeto (opcional)'
}: CreateProjectModalProps) {
  const { showError, showSuccess } = useToast();
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const [touched, setTouched] = useState(false);
  const [isCreating, setIsCreating] = useState(false);


  useEffect(() => {
    if (open) {
      setProjectName('');
      setProjectDescription('');
      setTouched(false);
      setIsNameValid(false);

    }
  }, [open]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setProjectName(newName);
    setIsNameValid(newName.trim().length > 0);
    if (!touched) setTouched(true);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProjectDescription(e.target.value);
  };

  const handleCreateProject = async () => {
    if (!isNameValid || isCreating) return;

    setIsCreating(true);


    try {
      // Dados atualizados para corresponder ao modelo esperado pelo backend (Title em vez de name)
      const projectData = {
        Title: projectName.trim(),
        Description: projectDescription.trim() || 'Sem descrição',
      };
      
      // Criar o projeto no backend
      const response = await api.post('/projects', projectData);
      
      
      // Extrair ID de maneira segura, verificando vários padrões possíveis de resposta
      // Tenta acessar o ID do projeto usando diferentes estruturas possíveis
      const projectId = response.data?.data?.id || 
                        response.data?.id || 
                        (response.data?.data && typeof response.data.data === 'string' ? response.data.data : null);
      
      
      if (!projectId) {
        console.error('ID do projeto não encontrado na resposta:', response.data);
        showError('Projeto criado, mas não foi possível obter seu ID. Verifique na lista de projetos.');
        onClose(); // Ainda fechar o modal, mas sem redirecionar
        return;
      }
      
      
      // Notificar o componente pai sobre o novo projeto com o ID correto
      showSuccess('Projeto criado com sucesso!');
      onProjectCreated(projectId);
      
      // Fechar o modal
      onClose();
    } catch (err: any) {
      console.error('Erro ao criar projeto:', err);
      
      // Mostrar a mensagem de erro detalhada do backend se disponível
      const errorMessage = err.response?.data?.message || err.message;
      const validationErrors = err.response?.data?.errors || [];
      
      let detailedError = errorMessage;
      if (validationErrors.length > 0) {
        detailedError += ': ' + validationErrors.join(', ');
      }
      
      showError(`Ocorreu um erro ao criar o projeto: ${detailedError}. Por favor, tente novamente.`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <ProjectDialog
      open={open}
      onClose={onClose}
      aria-labelledby="create-project-dialog-title"
    >
      <ProjectDialogTitle id="create-project-dialog-title">
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            top: 8, 
            color: 'white' 
          }}
          size="small"
          disabled={isCreating}
        >
          <CloseIcon />
        </IconButton>
      </ProjectDialogTitle>

      <DividerWrapper>
        <CustomDivider />
      </DividerWrapper>
      
      <ProjectDialogContent>
        <Box>
          <SectionLabel variant="subtitle2">
            Nome do projeto
            <RequiredMark>*</RequiredMark>
          </SectionLabel>
          <ProjectTitleField
            autoFocus
            fullWidth
            variant="outlined"
            value={projectName}
            onChange={handleNameChange}
            isError={touched && !isNameValid}
            placeholder={placeholderName}
            disabled={isCreating}
          />
          {touched && !isNameValid && (
            <WarningText>
              <WarningAmberIcon />
              <Typography variant="caption">O nome do projeto é obrigatório</Typography>
            </WarningText>
          )}
        </Box>
        
        <Box>
          <SectionLabel variant="subtitle2">
            Descrição
          </SectionLabel>
          <ProjectDescriptionField
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={projectDescription}
            onChange={handleDescriptionChange}
            placeholder={placeholderDescription}
            disabled={isCreating}
          />
        </Box>

      </ProjectDialogContent>

      <ProjectDialogActions>
        <CreateButton 
          fullWidth 
          onClick={handleCreateProject}
          isValid={isNameValid && !isCreating}
          disabled={isCreating}
        >
          {isCreating ? <CircularProgress size={24} color="inherit" /> : "Criar Projeto"}
        </CreateButton>
      </ProjectDialogActions>
    </ProjectDialog>
  );
}